import React from 'react';
import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const OffresSection: React.FC = () => {
  const navigate = useNavigate();

  const handleDiagnostic   = () => navigate('/diagnostic');
  const handleSystemeGlow  = () => navigate('/systeme-glow');

  const diagnosticFeatures = [
    'Audit de positionnement',
    'Analyse concurrence & marché',
    'Identification des blocages de conversion',
    'Roadmap stratégique personnalisée',
  ];

  const systemeGlowGroups = [
    {
      label: 'Semaines 1-2 : Stratégie',
      items: [
        'Positionnement différenciateur',
        'Message & storytelling de marque',
        'Stratégie de contenu sur 3 mois',
      ],
    },
    {
      label: 'Semaines 3-4 : Construction',
      items: [
        'Tunnel de vente complet',
        'Séquences email automatisées',
        '10 visuels + captions copywritées',
      ],
    },
    {
      label: 'Semaines 5-6 : Lancement',
      items: [
        'Automatisations (Airtable, Make, Brevo)',
        'Intégration écosystème complet',
        'Stratégie de lancement + handover personnalisé',
      ],
    },
  ];

  return (
    <section id="offres" className="section-lg">
      <div className="container mx-auto px-4" style={{ maxWidth: 1060 }}>

        {/* ── Header ──────────────────────────────────────── */}
        <div className="reveal text-center content-medium mb-14 lg:mb-20">
          <div className="flex justify-center mb-6">
            <span className="reveal-child badge-pill">
              <span className="badge-pill-dot" />
              Offres
            </span>
          </div>
          <h2
            className="reveal-child font-display text-foreground leading-[1.1]"
            style={{ fontSize: 'clamp(2rem, 5vw, 3.75rem)' }}
          >
            Ton escalier vers la croissance.
          </h2>
        </div>

        {/* ── Cards grid ──────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-start">

          {/* ─ Card 1 : Diagnostic Stratégique (light) ─── */}
          <div className="reveal flex flex-col p-8 rounded-2xl border border-border bg-card hover-lift">
            <p className="reveal-child text-xs font-body font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3">
              Offre d'entrée
            </p>
            <h3
              className="reveal-child font-display text-foreground mb-2"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}
            >
              Le Diagnostic Stratégique
            </h3>
            <div
              className="reveal-child font-display font-semibold text-foreground mb-3"
              style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', letterSpacing: '-0.02em' }}
            >
              590€
            </div>
            <p className="reveal-child text-sm text-muted-foreground leading-relaxed">
              L'électrochoc dont ton business a besoin.
            </p>
            <p className="reveal-child text-sm text-muted-foreground mb-4 leading-relaxed mt-2">
              En 2 heures, on dissèque ton business. On identifie les fuites de ton tunnel actuel et on trace ta roadmap.
            </p>

            <ul className="space-y-3 mb-8 flex-1">
              {diagnosticFeatures.map((feature, i) => (
                <li key={i} className="reveal-child flex items-start gap-3 text-sm text-foreground">
                  <div className="check-circle mt-0.5">
                    <Check className="w-3 h-3 text-primary" strokeWidth={2.5} />
                  </div>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {/* Bonus sérénité */}
            <div
              className="reveal-child rounded-xl p-4 mb-8"
              style={{
                background: 'hsl(var(--rose) / 0.06)',
                border: '1px solid hsl(var(--rose) / 0.15)',
              }}
            >
              <p className="text-xs font-semibold text-foreground mb-1">Bonus sérénité :</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Si nous décidons de construire ton système ensemble après l'audit, le montant du diagnostic est intégralement déduit de ton forfait Système Glow.
              </p>
            </div>

            <button
              onClick={handleDiagnostic}
              className="reveal-child w-full py-4 rounded-xl font-semibold text-sm border border-foreground text-foreground hover:bg-off-white transition-all duration-300"
            >
              Réserver mon Diagnostic
            </button>
          </div>

          {/* ─ Card 2 : Système Glow (dark premium) ───── */}
          <div
            className="reveal flex flex-col relative rounded-2xl p-8 hover-lift"
            style={{
              background: 'hsl(var(--dark))',
              boxShadow: '0 24px 64px rgba(37, 30, 60, 0.22), 0 4px 16px rgba(37, 30, 60, 0.14)',
            }}
          >
            {/* Subtle background orb inside the dark card */}
            <div
              className="absolute top-0 right-0 w-48 h-48 rounded-full pointer-events-none"
              style={{
                background: 'hsl(var(--rose) / 0.1)',
                filter: 'blur(60px)',
              }}
            />

            {/* Badge */}
            <span
              className="absolute -top-3.5 left-8 text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider"
              style={{
                background: 'hsl(var(--rose))',
                color: 'hsl(var(--white))',
              }}
            >
              Offre principale
            </span>

            <p
              className="reveal-child relative text-xs font-body font-semibold uppercase tracking-[0.15em] mb-3 mt-1"
              style={{ color: 'hsl(var(--white) / 0.65)' }}
            >
              Done-for-you complet
            </p>
            <h3
              className="reveal-child relative font-display mb-2"
              style={{
                fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                color: 'hsl(var(--white))',
              }}
            >
              Système Glow
            </h3>
            <div className="reveal-child relative flex items-baseline gap-3 mb-3">
              <span
                className="font-display font-semibold"
                style={{
                  fontSize: 'clamp(2rem, 4vw, 2.75rem)',
                  letterSpacing: '-0.02em',
                  color: 'hsl(var(--rose))',
                }}
              >
                3 800€
              </span>
              <span
                className="text-sm"
                style={{ color: 'hsl(var(--white) / 0.65)' }}
              >
                ou 3× 1 300€
              </span>
            </div>
            <p
              className="reveal-child relative text-sm mb-8 leading-relaxed"
              style={{ color: 'hsl(var(--white) / 0.75)' }}
            >
              Le « tout construit pour toi ». 6 semaines pour transformer ta marque en machine de guerre.
            </p>

            {/* Grouped features */}
            <div className="relative space-y-5 mb-8 flex-1">
              {systemeGlowGroups.map((group, gi) => (
                <div
                  key={gi}
                  className={gi > 0 ? 'pt-5' : ''}
                  style={gi > 0 ? { borderTop: '1px solid hsl(var(--white) / 0.1)' } : undefined}
                >
                  <p
                    className="reveal-child text-xs font-semibold uppercase tracking-wider mb-3"
                    style={{ color: 'hsl(var(--white) / 0.55)' }}
                  >
                    {group.label}
                  </p>
                  <ul className="space-y-2">
                    {group.items.map((item, ii) => (
                      <li
                        key={ii}
                        className="reveal-child flex items-start gap-3 text-sm"
                        style={{ color: 'hsl(var(--white) / 0.85)' }}
                      >
                        <div
                          className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center"
                          style={{ background: 'hsl(var(--rose) / 0.2)' }}
                        >
                          <Check
                            className="w-3 h-3"
                            strokeWidth={2.5}
                            style={{ color: 'hsl(var(--rose))' }}
                          />
                        </div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <button
              onClick={handleSystemeGlow}
              className="reveal-child relative w-full py-4 rounded-xl font-semibold text-sm transition-all duration-300 hover:scale-[1.02]"
              style={{
                background: 'hsl(var(--rose))',
                color: 'hsl(var(--white))',
                boxShadow: '0 4px 20px rgba(212, 100, 154, 0.35)',
              }}
            >
              Commencer mon Système Glow
            </button>
          </div>

        </div>
      </div>
    </section>
  );
};

export default OffresSection;
